import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Code2, Database, Server, Cpu, ShieldCheck, Lock, 
  ArrowRight, Sparkles, Rocket, Calendar
} from "lucide-react";
import { 
  SiNodedotjs, SiGo, SiPython, SiPostgresql, SiRedis, 
  SiDocker, SiKubernetes, SiGraphql, SiGooglecloud 
} from "react-icons/si";
import { FaAws } from "react-icons/fa6";

export default function EnterpriseSoftwareService() {
  const capabilities = [
    {
      icon: <Server className="text-blue-600 dark:text-cyan-400" size={24} />,
      title: "Microservices & Cloud Backend",
      desc: "Distributed microservices architectures built on Node.js, Go, or Python with Docker & Kubernetes orchestration."
    },
    {
      icon: <Database className="text-blue-600 dark:text-cyan-400" size={24} />,
      title: "High-Throughput Relational & NoSQL",
      desc: "Optimized database design using PostgreSQL, Redis caching, MongoDB, and ElasticSearch for instant query execution."
    },
    {
      icon: <Code2 className="text-blue-600 dark:text-cyan-400" size={24} />,
      title: "REST & GraphQL API Gateways",
      desc: "Secure, versioned API gateways with rate limiting, JWT authentication, OpenAPI documentation, and webhooks."
    },
    {
      icon: <Cpu className="text-blue-600 dark:text-cyan-400" size={24} />,
      title: "Custom CRM & Internal Portals",
      desc: "Tailored enterprise dashboards, workflow automation tools, employee portals, and real-time business intelligence reporting."
    },
    {
      icon: <Lock className="text-blue-600 dark:text-cyan-400" size={24} />,
      title: "End-to-End Encryption & Security",
      desc: "Bank-grade data encryption, SOC2 compliance standards, role-based access control (RBAC), and automated vulnerability audits."
    },
    {
      icon: <ShieldCheck className="text-blue-600 dark:text-cyan-400" size={24} />,
      title: "24/7 SLA Maintenance & DevOps",
      desc: "Dedicated SLA response guarantees, CI/CD pipeline automation, automated testing, and proactive server uptime monitoring."
    }
  ];

  const techStackData = [
    {
      name: "Node.js",
      category: "Backend Engine",
      icon: React.createElement(SiNodedotjs as any, { className: "text-[#5FA04E]", size: 32 })
    },
    {
      name: "Go",
      category: "Microservices Language",
      icon: React.createElement(SiGo as any, { className: "text-[#00ADD8]", size: 32 })
    },
    {
      name: "Python",
      category: "AI & Data Engine",
      icon: React.createElement(SiPython as any, { className: "text-[#3776AB]", size: 32 })
    },
    {
      name: "PostgreSQL",
      category: "Relational Database",
      icon: React.createElement(SiPostgresql as any, { className: "text-[#4169E1]", size: 32 })
    },
    {
      name: "Redis",
      category: "In-Memory Cache",
      icon: React.createElement(SiRedis as any, { className: "text-[#DC382D]", size: 32 })
    },
    {
      name: "Docker",
      category: "Containerization",
      icon: React.createElement(SiDocker as any, { className: "text-[#2496ED]", size: 32 })
    },
    {
      name: "Kubernetes",
      category: "Cloud Orchestration",
      icon: React.createElement(SiKubernetes as any, { className: "text-[#326CE5]", size: 32 })
    },
    {
      name: "GraphQL",
      category: "API Gateway Engine",
      icon: React.createElement(SiGraphql as any, { className: "text-[#E10098]", size: 32 })
    },
    {
      name: "AWS",
      category: "Cloud Infrastructure",
      icon: React.createElement(FaAws as any, { className: "text-[#FF9900]", size: 32 })
    },
    {
      name: "Google Cloud",
      category: "Enterprise GCP",
      icon: React.createElement(SiGooglecloud as any, { className: "text-[#4285F4]", size: 32 })
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#030712] text-slate-900 dark:text-slate-200 font-sans py-12 md:py-20 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
        
        {/* HERO */}
        <div className="text-center max-w-4xl mx-auto space-y-6">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 dark:bg-blue-500/15 border border-blue-200 dark:border-blue-500/30 text-blue-700 dark:text-blue-400">
            <Sparkles size={14} />
            <span className="text-xs font-bold uppercase tracking-wider">
              Enterprise Engineering
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-slate-900 dark:text-white tracking-tight leading-tight">
            💻 Custom <span className="text-blue-600 dark:text-cyan-400">Enterprise Software</span>
          </h1>

          <p className="text-slate-600 dark:text-slate-400 text-base sm:text-lg max-w-2xl mx-auto font-medium leading-relaxed">
            Scalable backend architectures, API gateways, custom internal portals, and cloud microservices built for high-concurrency enterprise workloads.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
            <Link 
              to="/Meeting" 
              className="h-12 px-6 rounded-xl font-semibold text-sm flex items-center gap-2 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 dark:bg-blue-500 dark:hover:bg-blue-600 !text-white shadow-lg shadow-blue-600/25 transition-all cursor-pointer"
            >
              <Calendar size={18} className="!text-white" />
              <span className="!text-white font-bold">Book Architect Call</span>
              <ArrowRight size={16} className="!text-white" />
            </Link>

            <Link 
              to="/enquirynow" 
              className="h-12 px-6 rounded-xl font-semibold text-sm flex items-center gap-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-800 text-slate-800 dark:text-slate-200 hover:border-blue-500 transition-all cursor-pointer shadow-sm"
            >
              <Rocket size={18} />
              <span>Get Enterprise Estimate</span>
            </Link>
          </div>
        </div>

        {/* VISUAL ENTERPRISE TECH STACK GRID */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-10 shadow-xl space-y-8">
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <span className="text-xs font-bold uppercase tracking-widest text-blue-600 dark:text-cyan-400 bg-blue-50 dark:bg-blue-500/10 px-3 py-1 rounded-full border border-blue-200 dark:border-blue-500/20">
              Cloud & Backend Ecosystem
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
              Enterprise Backend & Cloud Ecosystem
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm font-medium">
              We leverage cloud-native languages, databases, containerization, and orchestration tools.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {techStackData.map((tech, idx) => (
              <motion.div 
                key={idx}
                whileHover={{ scale: 1.04, y: -2 }}
                className="bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/60 p-4 rounded-2xl flex flex-col items-center justify-center text-center space-y-2.5 transition-all shadow-sm hover:shadow-md hover:border-blue-500/40 cursor-pointer"
              >
                <div className="w-12 h-12 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 flex items-center justify-center shadow-sm">
                  {tech.icon}
                </div>
                <div>
                  <h4 className="text-xs font-extrabold text-slate-900 dark:text-white leading-tight">{tech.name}</h4>
                  <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 block mt-0.5">{tech.category}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CAPABILITIES */}
        <div className="space-y-8">
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
              Enterprise Software Engineering
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm">
              Custom software solutions designed for reliability, security, and mission-critical performance.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map((cap, idx) => (
              <motion.div 
                key={idx}
                whileHover={{ y: -4 }}
                className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 space-y-4 shadow-md flex flex-col justify-between"
              >
                <div className="space-y-3">
                  <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-500/15 border border-blue-200 dark:border-blue-500/30 flex items-center justify-center">
                    {cap.icon}
                  </div>
                  <h3 className="text-lg font-extrabold text-slate-900 dark:text-white">
                    {cap.title}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm leading-relaxed">
                    {cap.desc}
                  </p>
                </div>

                <div className="pt-2 border-t border-slate-100 dark:border-slate-800/80">
                  <Link to="/enquirynow" className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-600 dark:text-cyan-400 hover:underline">
                    <span>Discuss Enterprise Specs</span>
                    <ArrowRight size={14} />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA BANNER */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 dark:from-blue-900/90 dark:to-indigo-950/90 text-white rounded-3xl p-8 sm:p-12 text-center space-y-6 shadow-2xl">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
            Schedule a 1-on-1 Engineering Discovery Call
          </h2>
          <p className="text-blue-100 max-w-xl mx-auto text-sm font-medium">
            Connect directly with our lead software architects to evaluate your technical requirements and receive an architectural roadmap.
          </p>
          <div className="flex flex-wrap justify-center gap-4 pt-2">
            <Link 
              to="/Meeting" 
              className="h-12 px-8 rounded-xl font-bold text-sm bg-white text-blue-700 hover:bg-slate-100 shadow-md flex items-center gap-2 cursor-pointer"
            >
              <span>Book Technical Consultation</span>
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
}
